import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DumbTask } from '#src/components/missions/DumbTask/index.tsx';

import { ThresholdsDuoTaskDialog } from './ThresholdsDuoTaskDialog/index.tsx';
import { ThresholdsDuoTaskScores } from './ThresholdsDuoTaskScores/index.tsx';
import type { ThresholdsDuoTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: ThresholdsDuoTask_taskInterface;
  featured?: boolean;
};

const StyledDumbTask = styled(DumbTask)`
  align-items: flex-start;
  @media ${laptopAndAbove} {
    align-items: center;
  }
`;

const Footer = styled(SBHorizontal)`
  width: 100%;
  @media ${laptopAndAbove} {
    width: auto;
  }
`;

export const ThresholdsDuoTask = ({ task, featured }: Props) => {
  if (!isType(task, 'ThresholdsDuoConnectionTask')) {
    return null;
  }

  const { rewardConfigs } = task;

  return (
    <StyledDumbTask
      featured={featured}
      borderColor={null}
      title={
        <Horizontal>
          <Avatar user={task.connectedUser} rounded size={2} />
          <Vertical>
            <LabelM bold>
              <UserName user={task.connectedUser} />
            </LabelM>
            <LabelM color="var(--c-nd-600)">{task.title}</LabelM>
          </Vertical>
        </Horizontal>
      }
      description={null}
      detailsDialog={({ open, onClose }) => (
        <ThresholdsDuoTaskDialog task={task} open={!!open} onClose={onClose} />
      )}
      footer={
        <Footer>
          <ThresholdsDuoTaskScores task={task} />
          <Horizontal gap={0.5}>
            {rewardConfigs.map(rewardConfig => (
              <AnyRewardConfig
                key={rewardConfig.id}
                anyRewardConfig={rewardConfig}
                variant={Variant.CHIP}
              />
            ))}
          </Horizontal>
        </Footer>
      }
    />
  );
};

ThresholdsDuoTask.fragments = {
  taskInterface: gql`
    fragment ThresholdsDuoTask_taskInterface on TaskInterface {
      id
      title
      color
      ... on ThresholdsDuoConnectionTask {
        id
        connectedUser {
          slug
          ...Avatar_user
          ...UserName_publicUserInfoInterface
        }
      }
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ...ThresholdsDuoTaskScores_taskInterface
      ...ThresholdsDuoTaskDialog_taskInterface
    }
    ${Avatar.fragments.user}
    ${UserName.fragments.user}
    ${ThresholdsDuoTaskScores.fragments.taskInterface}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ThresholdsDuoTaskDialog.fragments.taskInterface}
  ` as TypedDocumentNode<ThresholdsDuoTask_taskInterface>,
};
